import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomInput, CustomTextarea } from "@components/Forms";
import { Breadcrumb, CustomSelect } from "@components/UI";
import queryKeys from "@configs/react-query/query-key";
import ToastConstant from "@configs/react-toastify/snackbar";
import { showSuccessToast } from "@configs/react-toastify/snackbar";
import { LICENSE_TYPE_OPTIONS_NO_TRANSLATION } from "@constants/openSource";
import { TOAST } from "@constants/index";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { uppercase } from "@utils/common";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CHARACTER_LENGTH } from "@constants/index";

export default function ComponentAdd() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    componentName: "",
    componentVersion: "",
    cpe: "",
    licenseIdentifier: "",
    licenseName: "",
    licenseTypeCd: "",
    homepageUrl: "",
    downloadUrl: "",
    copyright: "",
    description: "",
    remark: "",
  });

  const [hasErrors, setHasErrors] = useState(true);

  // Validation
  useEffect(() => {
    const hasRequiredFields =
      formData.componentName.trim() !== "" &&
      formData.componentVersion.trim() !== "" &&
      formData.licenseIdentifier.trim() !== "";
    setHasErrors(!hasRequiredFields);
  }, [formData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCancel = () => {
    navigate("/components");
  };

  // API call for adding component
  const addComponent = useMutationData(
    queryKeys.addComponent,
    {
      method: "POST",
    },
    {
      onSuccess: async (response) => {
        showSuccessToast(t("open-source.component-add-success"));
        // Navigate to the detail page of the newly created component
        if (response && response.componentSeq) {
          navigate(`/components/${response.componentSeq}`);
        } else {
          navigate("/components");
        }
      },
      onError: (error) => {
        console.error("Error during create component:", error);
        const errorMessage = error?.response?.data || error?.message || "";
        if (errorMessage.includes("Component already exists with the same name, version, and license identifier.")) {
          ToastConstant(TOAST.error, t("open-source.component-already-exists"), "");
        } else {
          ToastConstant(TOAST.error, t("open-source.component-add-error"), "");
        }
      },
    },
  );

  const handleSave = async () => {
    if (!hasErrors) {
      // Trim all string values before sending
      const trimmedData = Object.keys(formData).reduce((acc, key) => {
        acc[key] = typeof formData[key] === "string" ? formData[key].trim() : formData[key];
        return acc;
      }, {});

      await addComponent.mutateAsync(trimmedData);
    }
  };

  return (
    <div className={`h-full ${colors.bgSecondary}`} data-testid="component-add-page">
      {/* Header with Breadcrumbs */}
      <div className="pb-3">
        <div className="space-y-2">
          <Breadcrumb
            items={[
              { label: t("open-source.component-list-title"), href: "/components" },
              { label: t("navbar.component-add") },
            ]}
          />
          <div className="flex items-center justify-between">
            <h1 className={`text-2xl font-bold ${colors.textPrimary}`}>
              {uppercase(t("open-source.add-component-button"))}
            </h1>
            <div className="flex items-center space-x-3">
              <OutlineButton data-testid="component-add-cancel-button" onClick={handleCancel}>{t("common.cancel-button")}</OutlineButton>
              <PrimaryButton data-testid="component-add-save-button" onClick={handleSave} disabled={hasErrors || addComponent.isPending}>
                {addComponent.isPending ? "Saving..." : t("common.save-button")}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>

      {/* Main Form Section */}
      <div data-testid="component-add-form" className={`rounded-lg border ${colors.borderPrimary} ${colors.bgPrimary} p-6`}>
        <div className="space-y-6 mx-auto max-w-[60rem]">
          {/* Component Information Group */}
          <div>
            <h3 className={`text-md font-semibold ${colors.textPrimary} pb-6`}>
              {uppercase(t("status.component-information-title"))}
            </h3>
            <div className="grid grid-cols-3 gap-4 pb-2">
              {/* Component Name */}
              <div>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`}>
                  {t("open-source.component-name")} <span className={colors.textRequired}>*</span>
                </label>
                <CustomInput
                  data-testid="component-add-name-input"
                  value={formData.componentName}
                  onChange={(e) => handleInputChange("componentName", e.target.value)}
                  placeholder={t("open-source.enter-component-name")}
                  size="md"
                  maxLength={CHARACTER_LENGTH.COMMENT}
                />
              </div>

              {/* Component Version */}
              <div>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`}>
                  {t("open-source.component-version")} <span className={colors.textRequired}>*</span>
                </label>
                <CustomInput
                  data-testid="component-add-version-input"
                  value={formData.componentVersion}
                  onChange={(e) => handleInputChange("componentVersion", e.target.value)}
                  placeholder={t("open-source.enter-component-version")}
                  size="md"
                  maxLength={CHARACTER_LENGTH.COMMENT}
                />
              </div>

              {/* CPE */}
              <div>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`}>
                  {t("open-source.cpe-detail")}
                </label>
                <CustomInput
                  value={formData.cpe}
                  onChange={(e) => handleInputChange("cpe", e.target.value)}
                  placeholder={t("open-source.enter-cpe")}
                  size="md"
                  maxLength={CHARACTER_LENGTH.COMMENT}
                />
              </div>
            </div>
          </div>

          {/* License Information Group */}
          <div>
            <h3 className={`text-md font-semibold ${colors.textPrimary} pb-6`}>
              {uppercase(t("status.license-information-title"))}
            </h3>
            <div className="grid grid-cols-3 gap-4">
              {/* License Identifier */}
              <div>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`}>
                  {t("license-policy.license-identifier")} <span className={colors.textRequired}>*</span>
                </label>
                <CustomInput
                  data-testid="component-add-license-identifier-input"
                  value={formData.licenseIdentifier}
                  onChange={(e) => handleInputChange("licenseIdentifier", e.target.value)}
                  placeholder={t("open-source.enter-license-identifier")}
                  size="md"
                  maxLength={CHARACTER_LENGTH.COMMENT}
                />
              </div>

              {/* License Name */}
              <div>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`}>
                  {t("license-policy.license-name")}
                </label>
                <CustomInput
                  value={formData.licenseName}
                  onChange={(e) => handleInputChange("licenseName", e.target.value)}
                  placeholder={t("open-source.enter-license-name")}
                  size="md"
                  maxLength={CHARACTER_LENGTH.COMMENT}
                />
              </div>

              {/* License Type */}
              <div>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`}>
                  {t("open-source.license-type")}
                </label>
                <div className="relative z-10">
                  <CustomSelect
                    options={LICENSE_TYPE_OPTIONS_NO_TRANSLATION.map((opt) => ({ value: opt.value, label: t(opt.label) }))}
                    value={
                      formData.licenseTypeCd
                        ? {
                          value: formData.licenseTypeCd,
                          label: t(LICENSE_TYPE_OPTIONS_NO_TRANSLATION.find((o) => o.value === formData.licenseTypeCd)?.label || ""),
                        }
                        : null
                    }
                    onChange={(opt) => handleInputChange("licenseTypeCd", opt?.value || "")}
                    placeholder={t("open-source.select-license-type")}
                    size="md"
                    menuPlacement="auto"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Links Group */}
          <div>
            <h3 className={`text-md font-semibold ${colors.textPrimary} pb-6 pt-2`}>
              {uppercase(t("open-source.links"))}
            </h3>
            <div className="space-y-4">
              {/* Homepage URL */}
              <div>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`}>
                  {t("open-source.homepage-url")}
                </label>
                <CustomInput
                  value={formData.homepageUrl}
                  onChange={(e) => handleInputChange("homepageUrl", e.target.value)}
                  placeholder={t("open-source.homepage-url-placeholder")}
                  size="md"
                  maxLength={2048}
                />
              </div>

              {/* Download URL */}
              <div>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`}>
                  {t("open-source.download-url")}
                </label>
                <CustomInput
                  value={formData.downloadUrl}
                  onChange={(e) => handleInputChange("downloadUrl", e.target.value)}
                  placeholder={t("open-source.download-url-placeholder")}
                  size="md"
                  maxLength={2048}
                />
              </div>
            </div>
          </div>

          {/* Descriptive Notes Group */}
          <div>
            <h3 className={`text-md font-semibold ${colors.textPrimary} pb-6 pt-2`}>
              {uppercase(t("open-source.descriptive-notes"))}
            </h3>
            <div className="space-y-4">
              {/* Copyright */}
              <div>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`}>
                  {t("open-source.copyright")}
                </label>
                <CustomTextarea
                  value={formData.copyright}
                  onChange={(e) => handleInputChange("copyright", e.target.value)}
                  rows={4}
                  placeholder={t("open-source.copyright-placeholder")}
                  maxLength={CHARACTER_LENGTH.COMMENT}
                  className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                />
                <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="component-add-copyright-char-count">
                  {(formData.copyright || "").length}/{CHARACTER_LENGTH.COMMENT}
                </p>
              </div>

              {/* Description */}
              <div>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`}>
                  {t("open-source.description-short")}
                </label>
                <CustomTextarea
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  rows={4}
                  placeholder={t("open-source.description-placeholder")}
                  maxLength={CHARACTER_LENGTH.CONTENT}
                  className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                />
                <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="component-add-description-char-count">
                  {(formData.description || "").length}/{CHARACTER_LENGTH.CONTENT}
                </p>
              </div>

              {/* Remark */}
              <div>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`}>
                  {t("license-policy.remark")}
                </label>
                <CustomTextarea
                  value={formData.remark}
                  onChange={(e) => handleInputChange("remark", e.target.value)}
                  rows={4}
                  placeholder={t("open-source.remark-placeholder")}
                  maxLength={CHARACTER_LENGTH.REMARK}
                  className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                />
                <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="component-add-remark-char-count">
                  {(formData.remark || "").length}/{CHARACTER_LENGTH.REMARK}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
